import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  FolderOpen, FileText, Calendar, Euro, Search, Download, 
  ChevronRight, ChevronDown, Check, CheckCircle, X, Eye,
  ArrowUpDown, Filter, Mail, Loader2, FolderClosed, Send
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export default function DocumentsExplorer({ token, onExportZip, onViewDocument }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDocs, setSelectedDocs] = useState(new Set());
  const [expandedFolders, setExpandedFolders] = useState(new Set(['all']));
  const [sortBy, setSortBy] = useState('date'); // date, amount, name
  const [sortOrder, setSortOrder] = useState('desc'); // asc, desc
  const [groupBy, setGroupBy] = useState('supplier'); // supplier, category, month
  const [preparingZip, setPreparingZip] = useState(false);
  
  // Email modal state
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    recipient_email: '',
    message: ''
  });
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    loadAllDocuments();
  }, [token]);

  const loadAllDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/accounting/documents/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Erreur chargement documents:', error);
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  // Filtrer les documents par recherche
  const filteredDocuments = useMemo(() => {
    if (!searchTerm) return documents;
    const search = searchTerm.toLowerCase();
    return documents.filter(doc => 
      (doc.title || '').toLowerCase().includes(search) ||
      (doc.supplier || '').toLowerCase().includes(search) ||
      (doc.reference || '').toLowerCase().includes(search) ||
      (doc.category_name || '').toLowerCase().includes(search)
    );
  }, [documents, searchTerm]);

  // Grouper les documents en dossiers
  const groupedDocuments = useMemo(() => {
    const groups = {};
    
    filteredDocuments.forEach(doc => {
      let key;
      let label;
      
      switch (groupBy) {
        case 'supplier':
          key = doc.supplier || 'Sans fournisseur';
          label = key;
          break;
        case 'category':
          key = doc.category_id || 'cat_autre';
          label = doc.category_name || 'Autre';
          break;
        case 'month':
          key = doc.date ? doc.date.slice(0, 7) : 'Sans date';
          label = key !== 'Sans date' 
            ? new Date(key + '-01').toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
            : 'Sans date';
          break;
        default:
          key = 'all';
          label = 'Tous les documents';
      }
      
      if (!groups[key]) {
        groups[key] = { key, label, documents: [], totalAmount: 0, sentCount: 0 };
      }
      groups[key].documents.push(doc);
      groups[key].totalAmount += doc.amount || 0;
      if (doc.sent_at) groups[key].sentCount++;
    });

    // Trier les documents dans chaque groupe
    Object.values(groups).forEach(group => {
      group.documents.sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
          case 'date':
            comparison = (a.date || '').localeCompare(b.date || '');
            break;
          case 'amount':
            comparison = (a.amount || 0) - (b.amount || 0);
            break;
          case 'name':
            comparison = (a.title || '').localeCompare(b.title || '');
            break;
        }
        return sortOrder === 'desc' ? -comparison : comparison;
      });
    });

    // Convertir en array et trier les groupes
    return Object.values(groups).sort((a, b) => {
      if (groupBy === 'month') {
        return sortOrder === 'desc' 
          ? b.key.localeCompare(a.key) 
          : a.key.localeCompare(b.key);
      }
      return a.label.localeCompare(b.label);
    });
  }, [filteredDocuments, groupBy, sortBy, sortOrder]);

  const toggleFolder = (key) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedFolders(newExpanded);
  };

  const toggleDocSelection = (docId) => {
    const newSelected = new Set(selectedDocs);
    if (newSelected.has(docId)) {
      newSelected.delete(docId);
    } else {
      newSelected.add(docId);
    }
    setSelectedDocs(newSelected);
  };

  const toggleFolderSelection = (folderKey) => {
    const folder = groupedDocuments.find(g => g.key === folderKey);
    if (!folder) return;
    
    const folderDocIds = folder.documents.map(d => d.id);
    const allSelected = folderDocIds.every(id => selectedDocs.has(id));
    
    const newSelected = new Set(selectedDocs);
    if (allSelected) {
      folderDocIds.forEach(id => newSelected.delete(id));
    } else {
      folderDocIds.forEach(id => newSelected.add(id));
    }
    setSelectedDocs(newSelected);
  };

  const selectAll = () => {
    const allIds = filteredDocuments.map(d => d.id);
    setSelectedDocs(new Set(allIds));
  };

  const deselectAll = () => {
    setSelectedDocs(new Set());
  };

  const handleExportZip = async () => {
    if (selectedDocs.size === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }

    setPreparingZip(true);
    try {
      const response = await axios.post(`${API}/api/ai-document/export/prepare-zip`, {
        document_ids: Array.from(selectedDocs),
        month: new Date().toISOString().slice(0, 7)
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });

      if (!response.data || !response.data.zip_data) {
        toast.error('Erreur: données ZIP invalides');
        return;
      }

      // Télécharger le ZIP
      const byteCharacters = atob(response.data.zip_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/zip' });
      
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.data.zip_filename || 'documents.zip';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`ZIP téléchargé: ${response.data.document_count} documents`);
    } catch (error) {
      console.error('Erreur export:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'export');
    } finally {
      setPreparingZip(false);
    }
  };

  const openEmailModal = () => {
    if (selectedDocs.size === 0) {
      toast.error('Sélectionnez au moins un document');
      return;
    }
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailData.recipient_email) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }

    // Validation email simple
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailData.recipient_email)) {
      toast.error('Adresse email invalide');
      return;
    }

    setSendingEmail(true);
    try {
      // Étape 1: Préparer le ZIP
      const prepareResponse = await axios.post(`${API}/api/ai-document/export/prepare-zip`, {
        document_ids: Array.from(selectedDocs),
        month: new Date().toISOString().slice(0, 7),
        recipient_email: emailData.recipient_email,
        message: emailData.message
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000
      });

      if (!prepareResponse.data?.export_id) {
        toast.error('Erreur lors de la préparation');
        return;
      }

      // Étape 2: Envoyer par email
      const sendResponse = await axios.post(`${API}/api/ai-document/export/send-email`, {
        export_id: prepareResponse.data.export_id,
        recipient_email: emailData.recipient_email,
        message: emailData.message
      }, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 60000
      });

      toast.success(`Email envoyé à ${emailData.recipient_email} (${selectedDocs.size} documents)`);
      setShowEmailModal(false);
      setEmailData({ recipient_email: '', message: '' });
      
      // Rafraîchir pour mettre à jour les statuts "envoyé"
      loadAllDocuments();
      
    } catch (error) {
      console.error('Erreur envoi email:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSendingEmail(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4">
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
            <Input
              type="text"
              placeholder="Rechercher un document..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
            />
          </div>

          {/* Group By */}
          <div className="flex items-center gap-2">
            <span className="text-white/60 text-sm">Grouper par:</span>
            <select
              value={groupBy}
              onChange={(e) => setGroupBy(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="supplier">Fournisseur</option>
              <option value="category">Catégorie</option>
              <option value="month">Mois</option>
            </select>
          </div>

          {/* Sort */}
          <div className="flex items-center gap-2">
            <span className="text-white/60 text-sm">Trier par:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="date">Date</option>
              <option value="amount">Montant</option>
              <option value="name">Nom</option>
            </select>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="text-white/60 hover:text-white"
            >
              <ArrowUpDown className="w-4 h-4" />
              {sortOrder === 'asc' ? '↑' : '↓'}
            </Button>
          </div>
        </div>

        {/* Selection Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={selectAll}
              className="text-white/60 hover:text-white"
            >
              <Check className="w-4 h-4 mr-1" />
              Tout sélectionner
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={deselectAll}
              className="text-white/60 hover:text-white"
            >
              <X className="w-4 h-4 mr-1" />
              Désélectionner
            </Button>
            <span className="text-white/40 text-sm ml-2">
              {selectedDocs.size} document(s) sélectionné(s)
            </span>
          </div>

          <div className="flex items-center gap-2">
            <Button
              onClick={openEmailModal}
              disabled={selectedDocs.size === 0 || sendingEmail}
              className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50"
            >
              {sendingEmail ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Mail className="w-4 h-4 mr-2" />
              )}
              Envoyer par email
            </Button>
            
            <Button
              onClick={handleExportZip}
              disabled={selectedDocs.size === 0 || preparingZip}
              className="bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50"
            >
              {preparingZip ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Télécharger ZIP ({selectedDocs.size})
            </Button>
          </div>
        </div>
      </div>

      {/* Stats summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-xl p-4">
          <div className="text-blue-300 text-sm">Total Documents</div>
          <div className="text-2xl font-bold text-white">{filteredDocuments.length}</div>
        </div>
        <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="text-emerald-300 text-sm">Dossiers</div>
          <div className="text-2xl font-bold text-white">{groupedDocuments.length}</div>
        </div>
        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 rounded-xl p-4">
          <div className="text-amber-300 text-sm">Montant Total</div>
          <div className="text-2xl font-bold text-white">
            {formatCurrency(filteredDocuments.reduce((sum, d) => sum + (d.amount || 0), 0))}
          </div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-4">
          <div className="text-purple-300 text-sm">Déjà Envoyés</div>
          <div className="text-2xl font-bold text-white">
            {filteredDocuments.filter(d => d.sent_at).length}
          </div>
        </div>
      </div>

      {/* Folders List */}
      <div className="space-y-2">
        {groupedDocuments.map((group) => {
          const isExpanded = expandedFolders.has(group.key);
          const folderDocIds = group.documents.map(d => d.id);
          const selectedInFolder = folderDocIds.filter(id => selectedDocs.has(id)).length;
          const allSelected = selectedInFolder === folderDocIds.length;
          
          return (
            <div 
              key={group.key}
              className="bg-white/5 backdrop-blur border border-white/10 rounded-xl overflow-hidden"
            >
              {/* Folder Header */}
              <div 
                className="flex items-center gap-3 p-4 cursor-pointer hover:bg-white/5 transition-colors"
                onClick={() => toggleFolder(group.key)}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFolderSelection(group.key);
                  }}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    allSelected 
                      ? 'bg-emerald-500 border-emerald-500' 
                      : selectedInFolder > 0
                      ? 'bg-emerald-500/50 border-emerald-500'
                      : 'border-white/30 hover:border-white/50'
                  }`}
                >
                  {(allSelected || selectedInFolder > 0) && (
                    <Check className="w-3 h-3 text-white" />
                  )}
                </button>

                {isExpanded ? (
                  <FolderOpen className="w-5 h-5 text-amber-400" />
                ) : (
                  <FolderClosed className="w-5 h-5 text-amber-400" />
                )}

                <div className="flex-1">
                  <span className="text-white font-medium">{group.label}</span>
                  <span className="text-white/40 text-sm ml-2">
                    ({group.documents.length} document{group.documents.length > 1 ? 's' : ''})
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm">
                  {group.sentCount > 0 && (
                    <span className="flex items-center gap-1 text-emerald-400">
                      <Mail className="w-4 h-4" />
                      {group.sentCount} envoyé{group.sentCount > 1 ? 's' : ''}
                    </span>
                  )}
                  <span className="text-white/60 font-mono">
                    {formatCurrency(group.totalAmount)}
                  </span>
                </div>

                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-white/40" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-white/40" />
                )}
              </div>

              {/* Documents List */}
              {isExpanded && (
                <div className="border-t border-white/10">
                  {group.documents.map((doc) => (
                    <div
                      key={doc.id}
                      className={`flex items-center gap-3 px-4 py-3 hover:bg-white/5 transition-colors border-b border-white/5 last:border-b-0 ${
                        selectedDocs.has(doc.id) ? 'bg-emerald-500/10' : ''
                      }`}
                    >
                      {/* Checkbox */}
                      <button
                        onClick={() => toggleDocSelection(doc.id)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                          selectedDocs.has(doc.id)
                            ? 'bg-emerald-500 border-emerald-500'
                            : 'border-white/30 hover:border-white/50'
                        }`}
                      >
                        {selectedDocs.has(doc.id) && (
                          <Check className="w-3 h-3 text-white" />
                        )}
                      </button>

                      {/* Icon */}
                      <FileText className="w-4 h-4 text-white/40" />

                      {/* Title & Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-white truncate">{doc.title || 'Sans titre'}</span>
                          {doc.sent_at && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                              <CheckCircle className="w-3 h-3" />
                              Envoyé
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-white/40 mt-0.5">
                          {doc.reference && <span>Réf: {doc.reference}</span>}
                          {doc.category_name && <span>{doc.category_name}</span>}
                        </div>
                      </div>

                      {/* Date */}
                      <div className="flex items-center gap-1 text-white/60 text-sm">
                        <Calendar className="w-4 h-4" />
                        {formatDate(doc.date)}
                      </div>

                      {/* Amount */}
                      <div className={`font-mono text-sm font-medium min-w-[100px] text-right ${
                        (doc.amount || 0) < 0 ? 'text-red-400' : 'text-emerald-400'
                      }`}>
                        {formatCurrency(doc.amount)}
                      </div>

                      {/* View Button */}
                      {doc.has_file && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewDocument && onViewDocument(doc)}
                          className="text-white/60 hover:text-white"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {groupedDocuments.length === 0 && (
          <div className="text-center py-12 text-white/40">
            <FolderOpen className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>Aucun document trouvé</p>
          </div>
        )}
      </div>

      {/* Modal Envoi par Email */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
              <Mail className="w-5 h-5 text-blue-400" />
              Envoyer par Email
            </h3>
            
            <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <p className="text-blue-300 text-sm">
                {selectedDocs.size} document(s) sélectionné(s) seront envoyés en pièce jointe (ZIP)
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-white/70 text-sm mb-1 block">Email du destinataire *</label>
                <Input
                  type="email"
                  value={emailData.recipient_email}
                  onChange={(e) => setEmailData({ ...emailData, recipient_email: e.target.value })}
                  placeholder="comptable@exemple.fr"
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
                />
              </div>

              <div>
                <label className="text-white/70 text-sm mb-1 block">Message (optionnel)</label>
                <textarea
                  value={emailData.message}
                  onChange={(e) => setEmailData({ ...emailData, message: e.target.value })}
                  placeholder="Bonjour, veuillez trouver ci-joint les documents comptables..."
                  rows={3}
                  className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-white placeholder:text-white/40 resize-none"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button
                onClick={() => setShowEmailModal(false)}
                variant="outline"
                className="flex-1 border-white/20 text-white"
                disabled={sendingEmail}
              >
                Annuler
              </Button>
              <Button
                onClick={handleSendEmail}
                disabled={sendingEmail || !emailData.recipient_email}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                {sendingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
